import { useCallback, useEffect, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { toast } from 'sonner';
import type {
  OrganisationTimesheetSettings,
  WorkingProfile,
} from '@/types/timesheetSettings';
import type { HealthCheckResult } from '@/types/healthCheck';
import type { ValidationError } from '@/hooks/useSettingsValidation';
import { runHealthCheck } from '@/services/settingsHealthCheck';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import { ApiError } from '@/services/apiClient';
import * as settingsRepository from '@/services/settingsRepository';
import { workingPatternToProfile } from '@/services/settingsRepository';
import { useSettings } from '@/contexts/SettingsContext';

export interface UseSettingsDataArgs {
  /** Applies loaded settings to the form field state (from useSettingsFormState). */
  hydrate: (settings: OrganisationTimesheetSettings) => void;
  workingProfile: WorkingProfile | null;
  setWorkingProfile: Dispatch<SetStateAction<WorkingProfile | null>>;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: Dispatch<SetStateAction<boolean>>;
  setTabsWithChanges: Dispatch<SetStateAction<Set<string>>>;
  setValidationErrors: Dispatch<SetStateAction<ValidationError[]>>;
}

export const useSettingsData = ({
  hydrate,
  workingProfile,
  setWorkingProfile,
  hasUnsavedChanges,
  setHasUnsavedChanges,
  setTabsWithChanges,
  setValidationErrors,
}: UseSettingsDataArgs) => {
  const { mode, isDemo, isLive } = useScreenDataMode('settings-general');
  const { refreshSettings } = useSettings();

  const [fullSettings, setFullSettings] = useState<OrganisationTimesheetSettings | null>(null);
  const [rowVersion, setRowVersion] = useState<string | undefined>(undefined);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Health Check state
  const [healthCheckResult, setHealthCheckResult] = useState<HealthCheckResult | null>(null);
  const [showHealthCheckDialog, setShowHealthCheckDialog] = useState(false);

  // Conflict resolution state
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [conflictServerSettings, setConflictServerSettings] = useState<OrganisationTimesheetSettings | null>(null);

  // Last saved state
  const [lastSaved, setLastSaved] = useState<{ timestamp: Date; user: string } | null>(null);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Fetch both timesheet settings and working pattern in parallel
      const [settings, workingPattern] = await Promise.all([
        settingsRepository.getOrganisationTimesheetSettings(mode),
        settingsRepository.getOrganisationWorkingPattern(mode),
      ]);

      setFullSettings(settings);

      // Apply the loaded settings to all field state and reset dirty tracking
      hydrate(settings);

      // Convert WorkingPattern from API to WorkingProfile format for the UI
      // This ensures we read from the same source (OrganizationWorkingPattern) that we write to
      const loadedWorkingProfile = workingPatternToProfile(workingPattern);
      setWorkingProfile(loadedWorkingProfile);

      if ('rowVersion' in settings) {
        setRowVersion(settings.rowVersion);
      }

      // Run health check after loading settings
      const healthResult = runHealthCheck(settings);
      setHealthCheckResult(healthResult);
    } catch (err) {
      console.error('Error loading settings:', err);
      const errorMessage = err instanceof ApiError
        ? err.message
        : 'Failed to load settings. Please try again.';
      setError(errorMessage);

      if (isLive) {
        toast.error('Failed to load settings', {
          description: 'Could not connect to API. Please ensure the backend is running or switch to demo mode.',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [mode]);

  // Warn user before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // The backend settings DTO does not include working pattern data, so
  // fromOrganisationTimesheetSettingsDto() always returns the DEFAULT working
  // profile (5-day / 40h).  After every save we must re-inject the real
  // workingProfile that was loaded from the OrganisationWorkingPattern API so
  // that fullSettings never reverts to the default.
  const setFullSettingsPreservingWorkingProfile = useCallback(
    (updated: OrganisationTimesheetSettings & { rowVersion?: string }) => {
      setFullSettings(prev => ({
        ...updated,
        workingProfile: workingProfile ?? prev?.workingProfile ?? updated.workingProfile,
      }));
      // Keep the localStorage cache in sync so other pages (homepage, approval
      // dashboard, etc.) also see the real working profile.
      if (workingProfile) {
        settingsRepository.setCachedLiveOrgSettings({ ...updated, workingProfile });
      }
    },
    [workingProfile],
  );

  const ensureLiveSettings = async (): Promise<{ settings: OrganisationTimesheetSettings; version: string } | null> => {
    if (fullSettings && rowVersion !== undefined) {
      return { settings: fullSettings, version: rowVersion };
    }
    try {
      const freshSettings = await settingsRepository.getOrganisationTimesheetSettings(mode);
      setFullSettings(freshSettings);
      const freshVersion = ('rowVersion' in freshSettings) ? (freshSettings.rowVersion ?? '') : undefined;
      if (freshVersion !== undefined) {
        setRowVersion(freshVersion);
        return { settings: freshSettings, version: freshVersion };
      }
    } catch {
      // fall through
    }
    toast.error('Cannot save', {
      description: 'Could not retrieve settings version from the API. Please reload the page.',
    });
    return null;
  };

  // Handle conflict resolution
  const handleConflictResolve = async (action: 'discard' | 'override') => {
    setShowConflictDialog(false);

    if (action === 'discard') {
      // Discard local changes and reload from server
      setHasUnsavedChanges(false);
      setTabsWithChanges(new Set());
      setValidationErrors([]);
      await loadData();
      toast.info('Changes discarded', {
        description: 'Your changes have been discarded and settings reloaded',
      });
    } else {
      // Override - force save with current settings
      // This would need to be implemented with a force flag in the API
      toast.warning('Override not yet implemented', {
        description: 'Please review the changes and save again',
      });
      await loadData();
    }
  };

  // Handle running health check on demand
  const handleRunHealthCheck = () => {
    if (fullSettings) {
      const result = runHealthCheck(fullSettings);
      setHealthCheckResult(result);
      setShowHealthCheckDialog(true);
    }
  };

  return {
    // Screen data mode
    mode, isDemo, isLive, refreshSettings,

    // Settings lifecycle state
    fullSettings, rowVersion,
    isLoading, error, isSaving, lastSaved,
    healthCheckResult, showHealthCheckDialog,
    showConflictDialog, conflictServerSettings,

    // Setters used by the page's save handlers
    setIsSaving, setLastSaved, setHealthCheckResult, setRowVersion,
    setConflictServerSettings, setShowConflictDialog, setShowHealthCheckDialog,

    // Functions
    loadData,
    setFullSettingsPreservingWorkingProfile,
    ensureLiveSettings,
    handleConflictResolve,
    handleRunHealthCheck,
  };
};

export type SettingsData = ReturnType<typeof useSettingsData>;
